/**
 * The shared printer service — one lifecycle coordinator above two drivers.
 *
 *   Application / Settings / Printer Helper
 *       → printerService                       (this module)
 *           → starPrinterAdapter               (the existing Star integration)
 *           → escPosPrinterAdapter             (generic ESC/POS over LAN, Electron)
 *
 * It owns what is common to every printer: which saved printer is active
 * (read from `usePrinterStore`), routing each call to that printer's driver,
 * one status subscription that tags events with the printer they describe,
 * and **job identity** — a job is captured against the printer that was
 * active when it was accepted and is never rerouted, and a job id presented
 * twice runs once.
 *
 * It owns nothing the drivers already own: Star keeps its connection, its
 * reconnect and its mutex inside `starPrintService`; ESC/POS keeps its sockets
 * and its poll in the Electron main process. There is no second reconnect
 * loop here — `usePrinterAutoReconnect` remains the only background one, and
 * it reaches every driver through `getStatus({ forceReconnect })`.
 */
import { getActivePrinter } from './active-printer-source';
import { starPrinterAdapter } from './adapters/star-printer.adapter';
import { escPosSetup, escPosPrinterAdapter } from './adapters/escpos-printer.adapter';
import {
  printerDriverOf,
  type SavedPrinter,
  type PrinterStatus,
  type PrinterAdapter,
  type PrintJobResult,
  type PrinterCapabilities,
  type PrinterStatusListener,
} from './printer-types';

/** Finished jobs remembered for duplicate detection; bounded, never a replay queue. */
const RECENT_JOBS_MAX = 128;
const RECENT_JOBS_TTL_MS = 10 * 60_000;

const ADAPTERS: Record<PrinterAdapter['driver'], PrinterAdapter> = {
  star: starPrinterAdapter,
  escpos: escPosPrinterAdapter,
};

export function adapterFor(printer: SavedPrinter): PrinterAdapter {
  return ADAPTERS[printerDriverOf(printer)];
}

function newJobId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `job-${random}`;
}

function noPrinterResult(jobId: string): PrintJobResult {
  return {
    success: false,
    outcome: 'not-sent',
    jobId,
    printerId: '',
    error: { code: 'NOT_CONNECTED', message: 'No printer is selected.', recoverable: true },
  };
}

function failed(jobId: string, printerId: string, error: unknown): PrintJobResult {
  const message = error instanceof Error ? error.message : String(error);
  const code = (error as { code?: string })?.code ?? 'PRINT_FAILED';
  return { success: false, outcome: 'not-sent', jobId, printerId, error: { code, message } };
}

export interface PrintImagesOptions {
  /** Names this physical print. Presenting it again returns the first outcome. */
  jobId?: string;
  openCashDrawer?: boolean;
  /** Rechecked by the driver before dispatch; a stale caller gets `cancelled`, not a print. */
  isCurrent?: () => boolean;
}

interface JobRequest {
  readonly jobId: string;
  readonly printer: SavedPrinter;
  readonly run: (adapter: PrinterAdapter, printer: SavedPrinter) => Promise<PrintJobResult>;
}

function createPrinterService() {
  const inFlight = new Map<string, Promise<PrintJobResult>>();
  const recent = new Map<string, { at: number; result: PrintJobResult }>();

  const rememberJob = (jobId: string, result: PrintJobResult): void => {
    const cutoff = Date.now() - RECENT_JOBS_TTL_MS;
    recent.forEach((entry, key) => {
      if (entry.at < cutoff) recent.delete(key);
    });
    while (recent.size >= RECENT_JOBS_MAX) {
      const oldest = recent.keys().next().value;
      if (oldest === undefined) break;
      recent.delete(oldest);
    }
    recent.set(jobId, { at: Date.now(), result });
  };

  /**
   * Accepts one job against the printer that is active **now**. The printer is
   * captured here, so a switch made while the job is queued or in flight can
   * neither redirect it nor interrupt it. A duplicate job id shares the
   * in-flight promise, or gets the recorded outcome once it has settled.
   */
  const submit = (request: JobRequest): Promise<PrintJobResult> => {
    const remembered = recent.get(request.jobId);
    if (remembered !== undefined) return Promise.resolve(remembered.result);
    const running = inFlight.get(request.jobId);
    if (running !== undefined) return running;

    const adapter = adapterFor(request.printer);
    const pending = request
      .run(adapter, request.printer)
      .catch((error: unknown) => failed(request.jobId, request.printer.identifier, error))
      .then((result) => {
        // A cancelled job never reached the printer, so its id stays available.
        if (!result.cancelled) rememberJob(request.jobId, result);
        return result;
      })
      .finally(() => {
        inFlight.delete(request.jobId);
      });
    inFlight.set(request.jobId, pending);
    return pending;
  };

  return {
    /** The printer every job currently routes to, or null. */
    getActivePrinter(): SavedPrinter | null {
      return getActivePrinter();
    },

    adapterFor,

    /** What `printer` (default: the active one) can do. */
    capabilities(printer: SavedPrinter | null = getActivePrinter()): PrinterCapabilities | null {
      return printer ? adapterFor(printer).capabilities(printer) : null;
    },

    /** Opens (or reuses) the active printer's connection. Throws when there is none. */
    async ensureConnected(): Promise<void> {
      const printer = getActivePrinter();
      if (!printer) throw new Error('No printer is selected.');
      await adapterFor(printer).ensureConnected(printer);
    },

    /** Explicitly closes `printer`'s connection (default: the active one). */
    async disconnect(printer: SavedPrinter | null = getActivePrinter()): Promise<void> {
      if (!printer) return;
      await adapterFor(printer).disconnect(printer);
    },

    /**
     * Reads the active printer's status, reconnecting when asked. This is the
     * one path the readiness probe and the background auto-reconnect loop use,
     * whichever driver the printer speaks.
     */
    async getStatus(options: { forceReconnect?: boolean } = {}): Promise<PrinterStatus> {
      const printer = getActivePrinter();
      if (!printer) throw new Error('No printer is selected.');
      return adapterFor(printer).getStatus(printer, options);
    },

    /**
     * Live status from every driver, tagged with the printer it describes.
     * `printerId` is null when the driver cannot name it (the mobile SDK).
     */
    subscribeStatus(listener: PrinterStatusListener): () => void {
      const unsubscribes = Object.values(ADAPTERS).map((adapter) => adapter.subscribeStatus(listener));
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    },

    /** Prints one or more bitmaps as one job on the active printer. Never throws. */
    printImages(images: readonly string[], options: PrintImagesOptions = {}): Promise<PrintJobResult> {
      const jobId = options.jobId ?? newJobId();
      const printer = getActivePrinter();
      if (!printer) return Promise.resolve(noPrinterResult(jobId));
      return submit({
        jobId,
        printer,
        run: (adapter, target) =>
          adapter.printImages(target, images, {
            jobId,
            openCashDrawer: options.openCashDrawer,
            isCurrent: options.isCurrent,
          }),
      });
    },

    /** Kicks the drawer through the active printer, as its own job. Never throws. */
    openCashDrawer(options: { jobId?: string } = {}): Promise<PrintJobResult> {
      const jobId = options.jobId ?? newJobId();
      const printer = getActivePrinter();
      if (!printer) return Promise.resolve(noPrinterResult(jobId));
      return submit({
        jobId,
        printer,
        run: (adapter, target) => adapter.openCashDrawer(target, { jobId }),
      });
    },

    /**
     * Prints a bitmap on a **specific** printer — the explicit test print of
     * the setup flows — on that printer only, never the active one.
     */
    printTestImageTo(printer: SavedPrinter, imageBase64: string): Promise<PrintJobResult> {
      const jobId = newJobId();
      return submit({
        jobId,
        printer,
        run: (adapter, target) =>
          adapter.printTestImage
            ? adapter.printTestImage(target, imageBase64, { jobId })
            : adapter.printImages(target, [imageBase64], { jobId }),
      });
    },

    /** ESC/POS setup: discovery, adapter choice and candidate checks. Desktop only. */
    escpos: escPosSetup,

    /** Number of jobs currently in flight (tests, diagnostics). */
    get inFlightJobs(): number {
      return inFlight.size;
    },

    /** Releases every live connection this service knows about. */
    async dispose(): Promise<void> {
      const printer = getActivePrinter();
      if (printer) await adapterFor(printer).disconnect(printer).catch(() => undefined);
    },
  };
}

export const printerService = createPrinterService();
export type PrinterService = typeof printerService;
